import type { ElectricityAnalysis } from '@popety-io/types'

// Re-export the type for convenience
export type { ElectricityAnalysis }

// Helper function to format price with translated unit
export const formatPrice = (price: number, unit = 'Rp./kWh'): string => {
  return `${price.toFixed(2)} ${unit}`
}

// Helper function to format CHF amount
export const formatCHF = (amount: number): string => {
  return `CHF ${amount.toLocaleString('de-CH', { maximumFractionDigits: 0 })}`
}

// Helper function to calculate percentage difference
export const calculatePercentageDifference = (
  value: number,
  reference: number
): number => {
  if (reference === 0) return 0
  return ((value - reference) / reference) * 100
}

// Helper function to get efficiency rating based on comparison
export const getPriceRating = (
  vsCantonMedian: number,
  vsNationalMedian: number
): 'high' | 'medium' | 'low' => {
  // "high" = low cost (good), "low" = high cost (bad)
  if (vsCantonMedian < -5 && vsNationalMedian < -5) return 'high'
  if (vsCantonMedian > 5 && vsNationalMedian > 5) return 'low'
  return 'medium'
}

// Helper function to get efficiency variant
export const getEfficiencyVariant = (
  rating: string
): 'high' | 'medium' | 'low' => {
  switch (rating) {
    case 'high':
      return 'high'
    case 'medium':
      return 'medium'
    case 'low':
      return 'low'
    default:
      return 'medium'
  }
}

// Helper to get evolution data for display
export const getEvolutionDisplay = (
  evolution: number | undefined
): { value: string; isPositive: boolean } => {
  if (evolution === undefined || evolution === null) {
    return { value: 'N/A', isPositive: false }
  }

  const isPositive = evolution <= 0 // Lower prices = positive for the user
  const sign = evolution > 0 ? '+' : ''

  return {
    value: `${sign}${evolution.toFixed(1)}%`,
    isPositive,
  }
}
